import { FC, useEffect, useState } from "react"
import { toast } from "react-toastify"

import { TournamentService } from "services/tournaments/tournament.service"

import { ChatMessage } from "types/tournament.types"

import { ChatFilters } from "./chat/ChatFilters"
import { ChatHeader } from "./chat/ChatHeader"
import { ChatMessageList } from "./chat/ChatMessageList"
import { ChatStats } from "./chat/ChatStats"
import { PlayerFilter } from "./chat/PlayerFilter"
import { SlowModeControl } from "./chat/SlowModeControl"

interface TournamentChatMonitorProps {
  tournamentId: string
  enableChat?: boolean
  chatSlowModeSeconds?: number
  onSlowModeChange?: (seconds: number) => void
  onToggleChat?: (enabled: boolean) => void
}

export const TournamentChatMonitor: FC<TournamentChatMonitorProps> = ({
  tournamentId,
  enableChat,
  chatSlowModeSeconds,
  onSlowModeChange,
  onToggleChat
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [showDeleted, setShowDeleted] = useState(true)
  const [onlyReported, setOnlyReported] = useState(false)
  const [filterPlayerId, setFilterPlayerId] = useState<string | null>(null)
  const [filterPlayerName, setFilterPlayerName] = useState<string>("")

  const loadMessages = async () => {
    try {
      const result = await TournamentService.getChatMessages(tournamentId, 1, 200, onlyReported, filterPlayerId || undefined)
      if (result?.messages) {
        setMessages(result.messages)
      }
    } catch {
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadMessages()

    const interval = setInterval(loadMessages, 5000)
    return () => clearInterval(interval)
  }, [tournamentId, onlyReported, filterPlayerId])

  const handleDelete = async (messageId: string) => {
    try {
      await TournamentService.deleteChatMessage(tournamentId, messageId)
      toast.success("Message deleted")
      await loadMessages()
    } catch {}
  }

  const handleRestore = async (messageId: string) => {
    try {
      await TournamentService.restoreChatMessage(tournamentId, messageId)
      toast.success("Message restored")
      await loadMessages()
    } catch {}
  }

  const handleFilterPlayer = (playerId: string, playerName: string) => {
    setFilterPlayerId(playerId)
    setFilterPlayerName(playerName)
  }

  const handleClearFilter = () => {
    setFilterPlayerId(null)
    setFilterPlayerName("")
  }

  const filteredMessages = messages.filter((m) => showDeleted || !m.is_deleted)

  return (
    <div className="p-6 bg-white dark:bg-[#0C0C1A] rounded-xl border border-[rgba(0,0,0,0.05)] dark:border-[rgba(255,255,255,0.1)] shadow-sm">
      <ChatHeader messageCount={messages.length} enableChat={enableChat} onToggleChat={onToggleChat} />

      <div className="flex items-center justify-end mb-4">
        <ChatFilters
          onlyReported={onlyReported}
          showDeleted={showDeleted}
          onOnlyReportedChange={setOnlyReported}
          onShowDeletedChange={setShowDeleted}
          onRefresh={loadMessages}
        />
      </div>

      {onSlowModeChange && chatSlowModeSeconds !== undefined && (
        <SlowModeControl currentSlowMode={chatSlowModeSeconds} onSlowModeChange={onSlowModeChange} />
      )}

      {filterPlayerId && <PlayerFilter playerName={filterPlayerName} onClear={handleClearFilter} />}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <span className="loading loading-spinner loading-md" />
        </div>
      ) : filteredMessages.length === 0 ? (
        <div className="text-center text-gray-500 dark:text-gray-400 py-12">No messages yet</div>
      ) : (
        <ChatMessageList
          messages={filteredMessages}
          showFilterButton={!filterPlayerId}
          onDelete={handleDelete}
          onRestore={handleRestore}
          onFilter={handleFilterPlayer}
        />
      )}

      <ChatStats messages={messages} />
    </div>
  )
}
